import { Client, Owner, OwnerOwnerCategoryEnum } from 'api'

export function removeOwnerFromList(owners: Owner[], idToRemove: number): Owner[] {
  // Remove the owner and all its descendants
  const idsToRemove = new Set<number>()
  function collectDescendants(id: number) {
    idsToRemove.add(id)
    owners.forEach((o) => {
      if (o.parentOwnerId === id && o.id !== undefined) collectDescendants(o.id)
    })
  }
  collectDescendants(idToRemove)
  return owners.filter((o) => o.id !== undefined && !idsToRemove.has(o.id))
}

export function updateOwnerById(owners: Owner[], updatedOwner: Owner): Owner[] {
  return owners.map((owner) => (owner.id === updatedOwner.id ? { ...owner, ...updatedOwner } : owner))
}

export function addOwnerToParent(owners: Owner[], parentId: number | null, newOwner: Owner): Owner[] {
  return [
    ...owners,
    {
      ...newOwner,
      parentOwnerId: parentId ?? undefined,
    },
  ]
}

export const getTotalSharePercentageClient = (client: Client, editId: number | null): number => {
  if (editId) {
    return getTotalSharePercentageOnClientOnEdit(client, editId)
  }
  return getTotalSharePercentageOnClientOnAdd(client)
}

const getTotalSharePercentageOnClientOnAdd = (client: Client): number => {
  return (
    client.ownersAttributes?.reduce((total, owner) => {
      if (owner.share && !owner.parentOwnerId) {
        return total + Number(owner.share)
      }
      return total
    }, 0) || 0
  )
}

const getTotalSharePercentageOnClientOnEdit = (client: Client, editId: number | null): number => {
  return (
    client.ownersAttributes?.reduce((total, owner) => {
      if (owner.share && !owner.parentOwnerId && owner.id !== editId) {
        return total + Number(owner.share)
      }
      return total
    }, 0) || 0
  )
}

export const getTotalSharePercentageOwner = (
  activeClient: Client,
  parentOwnerId: number,
  editId: number | null,
): number => {
  if (editId) {
    return getTotalSharePercentageOnEdit(activeClient, parentOwnerId, editId)
  }
  return getTotalSharePercentageOnAdd(activeClient, parentOwnerId)
}

const getTotalSharePercentageOnEdit = (
  activeClient: Client,
  parentOwnerId: number | null,
  editId: number | null,
): number => {
  return (
    activeClient.ownersAttributes?.reduce((total, shareholder) => {
      if (parentOwnerId === shareholder.parentOwnerId && shareholder.share && shareholder.id !== editId) {
        return total + Number(shareholder.share)
      }
      return total
    }, 0) || 0
  )
}

const getTotalSharePercentageOnAdd = (activeClient: Client, parentOwnerId: number | null): number => {
  return (
    activeClient.ownersAttributes?.reduce((total, shareholder) => {
      if (parentOwnerId === shareholder.parentOwnerId && shareholder.share) {
        return total + Number(shareholder.share)
      }
      return total
    }, 0) || 0
  )
}

export const getListOfOwnersWith25PercentShareOfClient = (activeClient: Client): Owner[] => {
  const owners = activeClient.ownersAttributes ?? []

  // Helper to build a map of owners by parentOwnerId
  const ownersByParent: Record<string, Owner[]> = {}
  owners.forEach((owner) => {
    const parentIdKey = String(owner.parentOwnerId ?? 'root')
    if (!ownersByParent[parentIdKey]) ownersByParent[parentIdKey] = []
    ownersByParent[parentIdKey].push(owner)
  })

  // Recursive function to traverse the tree and accumulate share percentages
  function collectIndividuals(parentId: number | undefined, accumulatedShare: number): Owner[] {
    const result: Owner[] = []
    const parentIdKey = String(parentId ?? 'root')
    const children = ownersByParent[parentIdKey] ?? []
    for (const child of children) {
      const share = Number(child.share) || 0
      const totalShare = accumulatedShare * (share / 100)
      if (child.ownerCategory === OwnerOwnerCategoryEnum.Individual && totalShare >= 0.25) {
        result.push({ ...child, share: totalShare })
      }
      // Continue traversing for all children (including individuals, in case of nested structures)
      result.push(...collectIndividuals(child.id, totalShare))
    }
    return result
  }

  // Start from root owners (parentOwnerId === undefined or null)
  return collectIndividuals(undefined, 1)
}
